if (!customElements.get('cart-drawer-quantity-input')) {
  class CartDrawerQuantityInput extends HTMLElement {
    static STOCK_ERROR_DURATION = 2500;
    static stockErrorState = null;
    static stockErrorTimer = null;

    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.input?.addEventListener('change', this.onInputChange.bind(this));
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    quantityUpdateUnsubscriber = undefined;

    connectedCallback() {
      this.validateQtyRules();
      CartDrawerQuantityInput.restoreStockErrorIfNeeded(this.input?.dataset.index);
      if (typeof subscribe === 'function') {
        this.quantityUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.quantityUpdate,
          this.validateQtyRules.bind(this)
        );
      }
    }

    disconnectedCallback() {
      if (this.quantityUpdateUnsubscriber) {
        this.quantityUpdateUnsubscriber();
      }
    }

    getEffectiveMin() {
      return Math.max(1, parseInt(this.input?.dataset.min || this.input?.min || '1', 10));
    }

    getEffectiveMax() {
      if (!this.input?.max) return null;
      const max = parseInt(this.input.max, 10);
      return Number.isNaN(max) ? null : max;
    }

    onInputChange() {
      const min = this.getEffectiveMin();
      let value = parseInt(this.input.value, 10);
      const previous = value;

      if (Number.isNaN(value) || value < min) {
        value = min;
        this.input.value = min;
      }

      const max = this.getEffectiveMax();
      if (max !== null && value > max) {
        this.input.value = max;
        this.showMaxStockError();
        value = max;
      }

      this.validateQtyRules();

      if (value !== previous) {
        this.input.dispatchEvent(this.changeEvent);
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      const button = event.currentTarget;
      const min = this.getEffectiveMin();
      const max = this.getEffectiveMax();
      const step = parseInt(this.input.step, 10) || 1;
      const current = parseInt(this.input.value, 10) || min;

      if (button.name === 'plus') {
        if (max !== null && current >= max) {
          this.showMaxStockError();
          return;
        }

        const next = current + step;
        if (max !== null && next > max) {
          this.input.value = max;
          this.showMaxStockError();
        } else {
          this.input.value = next;
          if (max !== null && next >= max) {
            this.showMaxStockError();
          }
        }
      } else if (current <= min) {
        this.input.value = min;
        this.validateQtyRules();
        return;
      } else {
        this.input.value = Math.max(min, current - step);
      }

      this.validateQtyRules();
      this.input.dispatchEvent(this.changeEvent);
    }

    showMaxStockError() {
      const lineIndex = this.input?.dataset.index;
      if (!lineIndex) return;

      CartDrawerQuantityInput.setStockErrorState(lineIndex);
      CartDrawerQuantityInput.displayStockError(lineIndex);
    }

    static setStockErrorState(lineIndex) {
      CartDrawerQuantityInput.stockErrorState = {
        lineIndex,
        hideAt: Date.now() + CartDrawerQuantityInput.STOCK_ERROR_DURATION,
      };
      CartDrawerQuantityInput.scheduleStockErrorHide();
    }

    static displayStockError(lineIndex) {
      const stockEl = document.getElementById(`CartDrawer-LineItemStockLevel-${lineIndex}`);
      if (!stockEl) return false;

      stockEl.classList.remove('hidden');
      stockEl.removeAttribute('hidden');
      stockEl.setAttribute('role', 'alert');
      return true;
    }

    static scheduleStockErrorHide() {
      clearTimeout(CartDrawerQuantityInput.stockErrorTimer);

      const state = CartDrawerQuantityInput.stockErrorState;
      if (!state) return;

      const remaining = state.hideAt - Date.now();
      if (remaining <= 0) {
        CartDrawerQuantityInput.clearStockErrorState();
        return;
      }

      CartDrawerQuantityInput.stockErrorTimer = setTimeout(() => {
        CartDrawerQuantityInput.clearStockErrorState();
      }, remaining);
    }

    static clearStockErrorState() {
      const state = CartDrawerQuantityInput.stockErrorState;
      if (state) {
        const stockEl = document.getElementById(`CartDrawer-LineItemStockLevel-${state.lineIndex}`);
        if (stockEl) {
          stockEl.classList.add('hidden');
          stockEl.setAttribute('hidden', '');
          stockEl.removeAttribute('role');
        }
      }

      CartDrawerQuantityInput.stockErrorState = null;
      clearTimeout(CartDrawerQuantityInput.stockErrorTimer);
      CartDrawerQuantityInput.stockErrorTimer = null;
    }

    static restoreStockErrorIfNeeded(lineIndex = null) {
      const state = CartDrawerQuantityInput.stockErrorState;
      if (!state || Date.now() > state.hideAt) {
        CartDrawerQuantityInput.clearStockErrorState();
        return;
      }

      if (lineIndex && lineIndex !== state.lineIndex) return;

      CartDrawerQuantityInput.displayStockError(state.lineIndex);
      CartDrawerQuantityInput.scheduleStockErrorHide();
    }

    validateQtyRules() {
      if (!this.input) return;

      const min = this.getEffectiveMin();
      const max = this.getEffectiveMax();
      let value = parseInt(this.input.value, 10);

      if (Number.isNaN(value) || value < min) {
        value = min;
        this.input.value = min;
      }

      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      const atMin = value <= min;
      if (buttonMinus) {
        buttonMinus.classList.toggle('disabled', atMin);
        buttonMinus.toggleAttribute('disabled', atMin);
      }

      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus && max !== null) {
        const atMax = value >= max;
        buttonPlus.classList.toggle('disabled', atMax);
        buttonPlus.setAttribute('aria-disabled', String(atMax));
      }
    }
  }

  customElements.define('cart-drawer-quantity-input', CartDrawerQuantityInput);
  window.restoreCartDrawerStockError = () => CartDrawerQuantityInput.restoreStockErrorIfNeeded();
}
